import { isValidUuidV7, MAX_SEARCH_TERM_LENGTH } from '@nudool/shared';

import {
  INVENTORY_COST_CHECK_LABELS,
  type InventoryCostCheck,
} from '../inventory-item-cost/index.js';
import { INVENTORY_ITEM_KIND_LABELS } from '../inventory-item-kind-labels/index.js';
import type { InventoryItemKind, InventoryItemMetricChip } from '../inventory-item-list-types.js';
import { INVENTORY_ITEM_METRIC_CHIP_LABELS } from '../inventory-item-metric-chip-labels.js';
import { isReorderFocusKey } from '../whats-short/is-reorder-focus-key.js';
import { isReorderStage } from '../whats-short/is-reorder-stage.js';
import type { ReorderStage } from '../whats-short/reorder-stage.js';

const MAX_REORDER_FOCUS_KEYS = 50;
const MAX_REORDER_FOCUS_LENGTH = MAX_REORDER_FOCUS_KEYS * 112;

export type InventoryItemsRouteSearch = {
  search?: string;
  brandIds?: string[];
  categoryIds?: string[];
  locationGroupIds?: string[];
  locationId?: string;
  partId?: string;
  stateIds?: string[];
  kind?: InventoryItemKind;
  activeOnly?: boolean;
  lotNumberContains?: string;
  metricChip?: InventoryItemMetricChip;
  uncategorized?: boolean;
  reorder?: true;
  reorderPartId?: string;
  reorderStage?: ReorderStage;
  reorderFocus?: string;
  variances?: true;
  /** The costing column set; absent means the stock view. */
  view?: 'cost';
  costCheck?: InventoryCostCheck;
};

function readStringParam(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

function readSearchParam(value: unknown): string | undefined {
  const text = readStringParam(value);
  return text !== undefined && text.length <= MAX_SEARCH_TERM_LENGTH ? text : undefined;
}

function readUuidParam(value: unknown): string | undefined {
  return typeof value === 'string' && isValidUuidV7(value) ? value : undefined;
}

function readKindParam(value: unknown): InventoryItemKind | undefined {
  return typeof value === 'string' && Object.hasOwn(INVENTORY_ITEM_KIND_LABELS, value)
    ? (value as InventoryItemKind)
    : undefined;
}

function readIdsParam(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const ids = value.filter(
    (item): item is string => typeof item === 'string' && isValidUuidV7(item),
  );
  return ids.length > 0 ? ids : undefined;
}

function readReorderFocusParam(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const keys = value
    .slice(0, MAX_REORDER_FOCUS_LENGTH)
    .split(',')
    .filter((key) => isReorderFocusKey(key))
    .slice(0, MAX_REORDER_FOCUS_KEYS);
  return keys.length > 0 ? keys.join(',') : undefined;
}

function readFlagParam(value: unknown): true | undefined {
  return value === 1 || value === '1' || value === true ? true : undefined;
}

function readMetricChipParam(value: unknown): InventoryItemMetricChip | undefined {
  return typeof value === 'string' && Object.hasOwn(INVENTORY_ITEM_METRIC_CHIP_LABELS, value)
    ? (value as InventoryItemMetricChip)
    : undefined;
}

function readCostCheckParam(value: unknown): InventoryCostCheck | undefined {
  return typeof value === 'string' && Object.hasOwn(INVENTORY_COST_CHECK_LABELS, value)
    ? (value as InventoryCostCheck)
    : undefined;
}

export function parseInventoryItemsRouteSearch(
  search: Record<string, unknown>,
): InventoryItemsRouteSearch {
  const locationGroupIds = readIdsParam(search.locationGroupIds);
  const view = search.view === 'cost' ? 'cost' : undefined;
  return {
    search: readSearchParam(search.search),
    brandIds: readIdsParam(search.brandIds),
    categoryIds: readIdsParam(search.categoryIds),
    locationGroupIds,
    // The warehouse rows only narrow to a bin inside its one selected warehouse, so a bin alone would split the row's status from them.
    locationId: locationGroupIds?.length === 1 ? readUuidParam(search.locationId) : undefined,
    partId: readUuidParam(search.partId),
    stateIds: readIdsParam(search.stateIds),
    kind: readKindParam(search.kind),
    activeOnly: search.activeOnly === false ? false : undefined,
    lotNumberContains: readSearchParam(search.lotNumberContains),
    metricChip: readMetricChipParam(search.metricChip),
    uncategorized: search.uncategorized === true ? true : undefined,
    reorder: readFlagParam(search.reorder),
    reorderPartId: readUuidParam(search.reorderPartId),
    reorderStage: isReorderStage(search.reorderStage) ? search.reorderStage : undefined,
    reorderFocus: readReorderFocusParam(search.reorderFocus),
    variances: readFlagParam(search.variances),
    view,
    costCheck: view ? readCostCheckParam(search.costCheck) : undefined,
  };
}

export function isAnyInventoryItemFilterActive(search: InventoryItemsRouteSearch): boolean {
  return Boolean(
    (search.brandIds && search.brandIds.length > 0) ||
    (search.categoryIds && search.categoryIds.length > 0) ||
    (search.locationGroupIds && search.locationGroupIds.length > 0) ||
    search.locationId ||
    search.partId ||
    (search.stateIds && search.stateIds.length > 0) ||
    search.activeOnly === false ||
    search.lotNumberContains ||
    search.metricChip ||
    search.uncategorized,
  );
}
